import type { RecordingUploadTrack } from '@/shared/api/recording/types';

import { isRetryableChunkUploadStatus, RecordingChunkUploadError } from '@/shared/api/recording/errors';

type UploadChunk = (params: {
  blob: Blob;
  chunkIndex: number;
  recordingId: string;
  signal: AbortSignal;
  track: RecordingUploadTrack;
}) => Promise<unknown>;

export type ChunkUploadQueueState = 'failed' | 'retrying' | 'uploading';

const CHUNK_REQUEST_TIMEOUT_MS = 60_000;
const MAX_UNSENT_BYTES = 200 * 1024 * 1024;
const CHUNK_RETRY_WINDOW_MS = 3 * 60_000;
const RETRY_BASE_DELAY_MS = 1_000;
const RETRY_MAX_DELAY_MS = 30_000;

const getChunkRetryDelayMs = (attempt: number) => Math.min(RETRY_BASE_DELAY_MS * 2 ** attempt, RETRY_MAX_DELAY_MS);

class UnsentDataLimitError extends Error {
  constructor() {
    super('The recording could not be uploaded fast enough and the unsent part reached its limit.');
    this.name = 'UnsentDataLimitError';
  }
}

const isRetryableError = (error: unknown) =>
  !(error instanceof RecordingChunkUploadError) || isRetryableChunkUploadStatus(error.status);

const createRequestSignal = (queueSignal: AbortSignal, timeoutMs: number) => {
  const controller = new AbortController();
  const abortWithQueue = () => controller.abort(queueSignal.reason);
  const timer = setTimeout(
    () => controller.abort(new DOMException('The chunk upload timed out.', 'TimeoutError')),
    timeoutMs,
  );
  queueSignal.addEventListener('abort', abortWithQueue, { once: true });

  return {
    dispose: () => {
      clearTimeout(timer);
      queueSignal.removeEventListener('abort', abortWithQueue);
    },
    signal: controller.signal,
  };
};

type ChunkUploadQueueOptions = {
  maxUnsentBytes?: number;
  requestTimeoutMs?: number;
  retryWindowMs?: number;
  onStateChange?: (state: ChunkUploadQueueState) => void;
};

export type ChunkUploadQueue = {
  abort: () => void;
  enqueue: (track: RecordingUploadTrack, blob: Blob) => void;
  isAborted: () => boolean;
  retry: () => void;
  waitForUploads: () => Promise<void>;
};

type UnsentChunk = { blob: Blob; chunkIndex: number };
type Waiter = { reject: (reason: unknown) => void; resolve: () => void };

const TRACKS: RecordingUploadTrack[] = ['recording', 'camera'];

/**
 * Uploads a recording's chunks while it is being recorded. Each track uploads its chunks one at a time and in
 * order; the two tracks upload independently. A failed chunk is sent again with the same index, with a growing
 * delay, and the chunks after it wait: the server appends chunks in the order they arrive, so none may be skipped.
 * The queue gives up (state 'failed') on a final server error, when a chunk keeps failing for too long or when too
 * much is left unsent. It then keeps every unsent chunk, so retry() can still save the whole recording.
 */
export const createChunkUploadQueue = (
  recordingId: string,
  uploadChunk: UploadChunk,
  {
    maxUnsentBytes = MAX_UNSENT_BYTES,
    requestTimeoutMs = CHUNK_REQUEST_TIMEOUT_MS,
    retryWindowMs = CHUNK_RETRY_WINDOW_MS,
    onStateChange,
  }: ChunkUploadQueueOptions = {},
): ChunkUploadQueue => {
  const controller = new AbortController();
  const unsentChunks: Record<RecordingUploadTrack, UnsentChunk[]> = { camera: [], recording: [] };
  const nextChunkIndexes: Record<RecordingUploadTrack, number> = { camera: 0, recording: 0 };
  const drainingTracks = new Set<RecordingUploadTrack>();
  const retryingTracks = new Set<RecordingUploadTrack>();
  const sleepers = new Map<() => void, ReturnType<typeof setTimeout>>();
  let unsentBytes = 0;
  let failure: { reason: unknown } | null = null;
  let waiters: Waiter[] = [];
  let state: ChunkUploadQueueState = 'uploading';

  const getState = (): ChunkUploadQueueState => {
    if (failure) return 'failed';
    return retryingTracks.size > 0 ? 'retrying' : 'uploading';
  };

  const updateState = () => {
    const nextState = getState();
    if (nextState === state || controller.signal.aborted) return;
    state = nextState;
    onStateChange?.(nextState);
  };

  const settleWaiters = () => {
    if (failure) {
      const { reason } = failure;
      waiters.forEach((waiter) => waiter.reject(reason));
    } else if (TRACKS.every((track) => unsentChunks[track].length === 0)) {
      waiters.forEach((waiter) => waiter.resolve());
    } else {
      return;
    }
    waiters = [];
  };

  const wakeAll = () => {
    sleepers.forEach((timer, wakeUp) => {
      clearTimeout(timer);
      wakeUp();
    });
    sleepers.clear();
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        sleepers.delete(resolve);
        resolve();
      }, ms);
      sleepers.set(resolve, timer);
    });

  const giveUp = (reason: unknown) => {
    if (failure) return;
    failure = { reason };
    wakeAll();
    updateState();
    settleWaiters();
  };

  const canUpload = () => !failure && !controller.signal.aborted;

  const drain = async (track: RecordingUploadTrack) => {
    if (drainingTracks.has(track)) return;
    drainingTracks.add(track);
    let attempt = 0;
    let failingSince: null | number = null;

    while (canUpload() && unsentChunks[track].length > 0) {
      const chunk = unsentChunks[track][0];
      const request = createRequestSignal(controller.signal, requestTimeoutMs);
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadChunk({
          blob: chunk.blob,
          chunkIndex: chunk.chunkIndex,
          recordingId,
          signal: request.signal,
          track,
        });
        unsentChunks[track].shift();
        unsentBytes -= chunk.blob.size;
        attempt = 0;
        failingSince = null;
        retryingTracks.delete(track);
        updateState();
        settleWaiters();
      } catch (error) {
        if (controller.signal.aborted) break;
        failingSince ??= Date.now();
        if (!isRetryableError(error) || Date.now() - failingSince >= retryWindowMs) {
          giveUp(error);
          break;
        }
        retryingTracks.add(track);
        updateState();
        // eslint-disable-next-line no-await-in-loop
        await sleep(getChunkRetryDelayMs(attempt));
        attempt += 1;
      } finally {
        request.dispose();
      }
    }

    drainingTracks.delete(track);
  };

  return {
    abort: () => {
      controller.abort();
      TRACKS.forEach((track) => {
        unsentChunks[track] = [];
      });
      unsentBytes = 0;
      wakeAll();
      settleWaiters();
    },
    enqueue: (track, blob) => {
      if (blob.size === 0 || controller.signal.aborted) return;

      unsentChunks[track].push({ blob, chunkIndex: nextChunkIndexes[track]++ });
      unsentBytes += blob.size;
      if (unsentBytes > maxUnsentBytes) giveUp(new UnsentDataLimitError());
      void drain(track);
    },
    isAborted: () => controller.signal.aborted,
    retry: () => {
      if (!failure || controller.signal.aborted) return;
      failure = null;
      retryingTracks.clear();
      updateState();
      TRACKS.forEach((track) => void drain(track));
    },
    waitForUploads: () =>
      new Promise<void>((resolve, reject) => {
        waiters.push({ reject, resolve });
        settleWaiters();
      }),
  };
};
